import { useState, useContext } from 'react';
import { Modal, message, Tooltip } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import Iconfont from '@utils/Iconfont';
import PSAIModal from '@PSAIComponents/PSAIModal';
import { AppContext } from '@utils/AppContext';
import FolderNameModal from '../FolderNameModal';
import MoveFolderModal from '../MoveFolderModal';
import { MaterialManageContext } from '../../hooks/MaterialManageContext';
import API from '@api/api';
import styles from './index.module.scss';

/**
 * 参考图查看大图弹窗
 * @param {boolean} open - 是否显示
 * @param {object} item - 参考图数据
 * @param {function} onClose - 关闭回调
 * @param {function} onRefresh - 操作后刷新列表回调
 */
const ImagePreviewModal = ({ open, item, onClose, onRefresh }) => {
    const { globalOrgInfo } = useContext(AppContext);
    const { activeTab, activeMenu, searchKeyword, fetchMaterialList, menuList } = useContext(MaterialManageContext);
    const [renameOpen, setRenameOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    if (!item) return null;

    const isPersonal = globalOrgInfo?.isPersonal;
    const canManage = item.canManage !== false;

    const handleShare = (e) => {
        e?.stopPropagation?.();
        setShareOpen(true);
    };

    const handleMoveClick = (e) => {
        e?.stopPropagation?.();
        setMoveOpen(true);
    };

    const handleDelete = (e) => {
        e?.stopPropagation?.();
        PSAIModal.confirm({
            content: (
                <div style={{ textAlign: 'center' }}>
                    <p>确定要删除此参考图吗？</p>
                    <p style={{ color: '#FF3B30' }}>此操作不可复原，请确认是否删除</p>
                </div>
            ),
            onOk: () => {
                return API.materialResDelete({ resIds: [item.id] })
                    .then((res) => {
                        if (res.code === 0) {
                            message.success('删除成功');
                            onClose?.();
                            onRefresh?.();
                        } else {
                            message.error(res.message || '删除失败');
                        }
                    })
                    .catch(() => {
                        message.error('删除失败，请稍后重试');
                    });
            },
        });
    };

    const handleRenameSuccess = () => {
        setRenameOpen(false);
        onRefresh?.();
    };

    const handleMoveSuccess = () => {
        setMoveOpen(false);
        onClose?.();
        onRefresh?.();
    };

    const folderName = item.folderPath ? item.folderPath.split('/').pop() : '全部参考图';

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                closable={false}
                centered
                width="auto"
                className={styles.previewModal}
                zIndex={1000}
                destroyOnClose
            >
                <div className={styles.previewContent}>
                    {/* 左侧图片区域 */}
                    <div className={styles.imageArea}>
                        <img src={item.url || item.imageUrl} alt={item.name} />
                        {/* 图片右上角操作按钮 */}
                        {canManage && (
                            <div className={styles.imageActions}>
                                {!isPersonal && activeTab !== 'enterprise' && (
                                    <Tooltip title="分享到企业" mouseEnterDelay={0.5}>
                                    <div className={styles.actionBtn} onClick={(e) => handleShare(e)}>
                                        <Iconfont type="icon-Material-MoveToEnterpriseOutlined" />
                                    </div>
                                </Tooltip>
                            )}
                            <Tooltip title="移动" mouseEnterDelay={0.5}>
                                <div className={styles.actionBtn} onClick={(e) => handleMoveClick(e)}>
                                    <Iconfont type="icon-Material-FolderMoveOutlined" />
                                </div>
                            </Tooltip>
                            <Tooltip title="删除" mouseEnterDelay={0.5}>
                                <div className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={(e) => handleDelete(e)}>
                                    <Iconfont type="icon-Material-DeleteOutlined" />
                                </div>
                            </Tooltip>
                            </div>
                        )}
                    </div>
                    {/* 右侧信息区域 */}
                    <div className={styles.infoArea}>
                        <div className={styles.infoHeader}>
                            <span className={styles.infoTitle}>参考图信息</span>
                            <CloseOutlined className={styles.closeBtn} onClick={onClose} />
                        </div>
                        <div className={styles.infoBody}>
                            <div className={styles.nameRow}>
                                <span className={styles.nameText}>{item.name}</span>
                                {canManage && (
                                    <Tooltip title="重命名" mouseEnterDelay={0.5}>
                                        <EditOutlined
                                            className={styles.editIcon}
                                            onClick={() => setRenameOpen(true)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>目录</span>
                                <span className={styles.value}>{folderName}</span>
                            </div>
                            <div className={styles.infoRow}>
                                <span className={styles.label}>创建时间</span>
                                <span className={styles.value}>{item.createdTime || '-'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            <FolderNameModal
                open={renameOpen}
                mode="renameRes"
                initialName={item.name || ''}
                renameId={item.id}
                zIndex={2000}
                onSuccess={handleRenameSuccess}
                onCancel={() => setRenameOpen(false)}
            />
            <MoveFolderModal
                open={moveOpen}
                mode="res"
                menuList={menuList}
                resIds={[item.id]}
                zIndex={2000}
                onSuccess={handleMoveSuccess}
                onCancel={() => setMoveOpen(false)}
            />
            <MoveFolderModal
                open={shareOpen}
                mode="share"
                resIds={[item.id]}
                shareScope="enterprise"
                zIndex={2000}
                onSuccess={() => setShareOpen(false)}
                onCancel={() => setShareOpen(false)}
            />
        </>
    );
};

export default ImagePreviewModal;
